import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { keycloak } from '../utils/keycloak';

export interface KeycloakUser {
  email?: string;
  name?: string;
  username?: string;
  sub?: string;
}

interface AuthContextType {
  authenticated: boolean;
  loading: boolean;
  user: KeycloakUser | null;
  token: string | null;
  login: () => Promise<void>;
  logout: () => void;
  hasRole: (role: string) => boolean;
  keycloak: typeof keycloak;
  ensureAuth: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Track if keycloak has been initialized
let keycloakInitialized = false;
let keycloakInitPromise: Promise<boolean> | null = null;

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(false); // Start as false - not loading until needed
  const [user, setUser] = useState<KeycloakUser | null>(null);
  const [token, setToken] = useState<string | null>(null);

  // Initialize keycloak only when needed
  const initKeycloak = useCallback(async (): Promise<boolean> => {
    // Already initialized
    if (keycloakInitialized) {
      return keycloak.authenticated || false;
    }

    // Already initializing - wait for it
    if (keycloakInitPromise) {
      return keycloakInitPromise;
    }

    setLoading(true);

    keycloakInitPromise = keycloak
      .init({
        onLoad: 'check-sso',
        checkLoginIframe: false,
      })
      .then((auth) => {
        keycloakInitialized = true;
        setAuthenticated(auth);

        if (auth) {
          setToken(keycloak.token || null);
          setUser({
            email: keycloak.tokenParsed?.email,
            name: keycloak.tokenParsed?.name,
            username: keycloak.tokenParsed?.preferred_username,
            sub: keycloak.tokenParsed?.sub,
          });

          keycloak.onTokenExpired = () => {
            keycloak.updateToken(30).then((refreshed) => {
              if (refreshed) setToken(keycloak.token || null);
            }).catch(() => keycloak.logout());
          };
        }

        setLoading(false);
        return auth;
      })
      .catch((error) => {
        console.error('Keycloak init error:', error);
        keycloakInitialized = true;
        setLoading(false);
        setAuthenticated(false);
        return false;
      });

    return keycloakInitPromise;
  }, []);

  // Ensure auth is initialized - call this before accessing protected routes
  const ensureAuth = useCallback(async (): Promise<boolean> => {
    const auth = await initKeycloak();
    return auth;
  }, [initKeycloak]);

  const login = useCallback(async () => {
    await initKeycloak();
    keycloak.login();
  }, [initKeycloak]);

  const logout = useCallback(() => {
    keycloak.logout();
  }, []);

  const hasRole = useCallback((role: string): boolean => {
    return keycloak.hasRealmRole(role);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        authenticated,
        loading,
        user,
        token,
        login,
        logout,
        hasRole,
        keycloak,
        ensureAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
